import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { API } from "../config/api.js";
import { get } from "../lib/http.js";
import { useAuth } from "../context/AuthContext.jsx";
import {
  fetchMyOrganizerApplication,
  isApproved,
  isSuperadmin,
} from "../organizer/organizerApplication.js";
import OrganizerConsoleHeader from "../organizer/OrganizerConsoleHeader.jsx";
import { formatEventDate } from "../utils/datetime.js";

const approvalBadge = (event) => {
  const status = String(event?.approvalStatus || "approved").toLowerCase();
  if (status === "approved") return { label: "Approved", classes: "bg-emerald-100 text-emerald-800" };
  if (status === "rejected") return { label: "Rejected", classes: "bg-rose-100 text-rose-800" };
  return { label: "Pending Approval", classes: "bg-amber-100 text-amber-900" };
};

const dateLabel = (value) => {
  if (!value) return "Date to be announced";
  const label = formatEventDate(value);
  return label === "—" ? "Date to be announced" : label;
};

/** Organizer events console (approved organizers only). */
export default function ListYourShowPage() {
  const { user, updateUser } = useAuth() || {};
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [orgApp, setOrgApp] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const superadmin = isSuperadmin();
  const orgApproved = superadmin || isApproved(orgApp);
  const createTo = orgApproved ? "/listyourshow/new" : "/listyourshow/account-setup";
  const createLabel = orgApproved ? "Create your event" : "Complete account setup";
  const organizerId = user?.organizerId || orgApp?.organizerId || "";

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const session = JSON.parse(localStorage.getItem("bb_user") || "null");
        if (!session?.token) throw new Error("Your session is unavailable. Please sign in again.");
        const response = await get(API.events.mySubmissions);
        if (active) setEvents(Array.isArray(response?.events) ? response.events : []);
      } catch (requestError) {
        if (active) setError(requestError.message || "Could not load your events.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (superadmin) {
      setOrgLoading(false);
      setOrgApp(null);
      return undefined;
    }
    let active = true;
    fetchMyOrganizerApplication()
      .then((app) => {
        if (!active) return;
        setOrgApp(app);
        if (app?.organizerId && typeof updateUser === "function") {
          const currentId = JSON.parse(localStorage.getItem("bb_user") || "null")?.organizerId;
          if (currentId !== app.organizerId) updateUser({ organizerId: app.organizerId });
        }
      })
      .catch(() => {
        if (active) setOrgApp(null);
      })
      .finally(() => {
        if (active) setOrgLoading(false);
      });
    return () => {
      active = false;
    };
  }, [superadmin]);

  const visibleEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          filter === "all" ||
          String(event.approvalStatus || "approved").toLowerCase() === filter
      ),
    [events, filter]
  );

  if (!superadmin && !orgLoading && !orgApproved) {
    return <Navigate to="/listyourshow/account-setup" replace />;
  }

  if (!superadmin && orgLoading) {
    return (
      <div className="min-h-screen bg-[#fffdf8]">
        <div className="mx-auto max-w-3xl px-4 py-20">
          <div className="h-64 animate-pulse rounded-2xl bg-slate-100" aria-label="Checking verification status" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffdf8]">
      <OrganizerConsoleHeader organizerId={organizerId} />

      <section id="my-events" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Your console</p>
            <h2 className="mt-2 text-3xl font-black text-slate-900">My events</h2>
            <p className="mt-2 text-sm text-slate-500">Track review status and keep every submission in one place.</p>
          </div>
          <Link
            to={createTo}
            className="inline-flex w-fit items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-amber-600"
          >
            {orgApproved ? "+ Create Event" : "Complete account setup"}
          </Link>
        </div>

        <div className="mt-7 flex gap-2 overflow-x-auto pb-2">
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["approved", "Approved"],
            ["rejected", "Rejected"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition ${
                filter === value
                  ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-900/20"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-amber-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>
        )}

        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {loading &&
            [0, 1, 2].map((item) => (
              <div key={item} className="h-48 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          {!loading &&
            visibleEvents.map((event) => {
              const id = event._id || event.id;
              const badge = approvalBadge(event);
              const approved = String(event.approvalStatus || "approved").toLowerCase() === "approved";
              return (
                <article
                  key={id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <div className="flex gap-4 p-4">
                    <div className="h-28 w-28 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-amber-100 to-orange-100">
                      {event.imageUrl ? (
                        <img src={event.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="grid h-full place-items-center text-3xl">🎟</div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-extrabold ${badge.classes}`}>
                        {badge.label}
                      </span>
                      <h3 className="mt-2 truncate text-lg font-black text-slate-900">
                        {event.name || "Untitled event"}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        {dateLabel(event.date)} · {event.venue?.city || "Venue TBA"}
                      </p>
                      {event.approvalStatus === "rejected" && event.rejectionReason && (
                        <p className="mt-2 line-clamp-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-800">
                          <strong>Reason:</strong> {event.rejectionReason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 border-t bg-slate-50 px-4 py-3">
                    <Link
                      to={`/listyourshow/view/${id}`}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-amber-400"
                    >
                      View
                    </Link>
                    <Link
                      to={`/listyourshow/edit/${id}`}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-amber-400"
                    >
                      Edit
                    </Link>
                    {approved ? (
                      <Link
                        to={`/events/dashboard/${id}`}
                        className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600"
                      >
                        Dashboard
                      </Link>
                    ) : (
                      <span className="self-center text-xs font-medium text-slate-400">
                        Dashboard unlocks after approval
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
        </div>
        {!loading && !visibleEvents.length && !error && (
          <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
            <div className="text-4xl">🎪</div>
            <h3 className="mt-3 font-bold text-slate-900">No events here yet</h3>
            <p className="mt-1 text-sm text-slate-500">Create an event and we’ll guide it through review.</p>
            <Link
              to={createTo}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-amber-600"
            >
              {createLabel} <FiArrowRight />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
